import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import PropertyCarousel from "@/components/PropertyCarousel";
import { Button } from "@/components/ui/button";
import { Users, TrendingUp, CheckCircle, AlertCircle, Plus } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AgentDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [myLeads, setMyLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isSM = user.role === "sourcing_manager";

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Refetch when sourcing manager changes context
  useEffect(() => {
    const handler = () => fetchDashboardData();
    window.addEventListener('context-changed', handler);
    return () => window.removeEventListener('context-changed', handler);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`, { headers }),
        axios.get(`${API}/leads`, { headers }),
      ]);

      setStats(statsRes.data);
      setMyLeads(leadsRes.data.slice(0, 10));
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {isSM ? 'Sourcing Manager Dashboard' : 'Agent Dashboard'}
          </h1>
          <p className="text-slate-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isSM ? 'Manage your properties, leads and developer mandates' : 'Manage your assigned leads and track performance'}
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="metric-card" data-testid="metric-total-leads">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">My Leads</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{stats?.total_leads || 0}</p>
              </div>
              <Users className="h-12 w-12 text-blue-900 opacity-20" strokeWidth={1.5} />
            </div>
          </div>

          <div className="metric-card" data-testid="metric-new-leads">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">New Leads</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{stats?.new_leads || 0}</p>
              </div>
              <AlertCircle className="h-12 w-12 text-blue-600 opacity-20" strokeWidth={1.5} />
            </div>
          </div>

          <div className="metric-card" data-testid="metric-qualified-leads">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Qualified</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{stats?.qualified_leads || 0}</p>
              </div>
              <CheckCircle className="h-12 w-12 text-blue-900 opacity-20" strokeWidth={1.5} />
            </div>
          </div>

          <div className="metric-card" data-testid="metric-conversion-rate">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">
                  {isSM ? 'My Properties' : 'Conversion Rate'}
                </p>
                <p className="text-3xl font-bold text-slate-900 mt-2">
                  {isSM ? (stats?.total_properties || 0) : `${stats?.conversion_rate || 0}%`}
                </p>
              </div>
              <TrendingUp className="h-12 w-12 text-amber-600 opacity-20" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        {/* Property Carousel */}
        <div className="mb-8">
          <PropertyCarousel />
        </div>

        {/* My Leads */}
        <div className="bg-white rounded-md border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                My Leads
              </h2>
              <Button
                data-testid="view-all-leads-button"
                onClick={() => navigate("/leads")}
                className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm"
              >
                View All Leads
              </Button>
            </div>
          </div>
          <div className="p-6">
            {myLeads.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No leads assigned yet</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {myLeads.map((lead) => (
                  <div
                    key={lead.id}
                    data-testid={`lead-item-${lead.id}`}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-sm hover:bg-slate-100 transition-colors cursor-pointer border border-slate-200"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                  >
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{lead.name}</p>
                      <p className="text-sm text-slate-600">{lead.email} • {lead.phone}</p>
                      {lead.property_title && (
                        <p className="text-xs text-amber-600 mt-1">Interested in: {lead.property_title}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {lead.ai_score && (
                        <div className="text-right">
                          <p className="text-xs text-slate-600 uppercase tracking-wide">AI Score</p>
                          <p className="text-lg font-bold text-blue-900">{lead.ai_score.toFixed(0)}</p>
                        </div>
                      )}
                      <span className={`status-badge status-${lead.status}`}>{lead.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
